import { isPublicSharePath } from '../lib/jobs/jobShareLinks';
import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { onAuthStateChanged, signOut, User, updateProfile } from 'firebase/auth';
import { auth, db, ensureAuthReady, appCheckSettled } from '../lib/firebase';
import { doc, getDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { upsertEmployerProfile } from '../lib/employerRepo';
import { plansEnabled } from '../config/featureFlags';
import { writeAuthHint } from '../lib/authHint';
import { setDemoViewer } from '../lib/demoAccounts';

type Role = 'emt' | 'employer' | null;
type Profile = any;

/** Firestore is source of truth: EMT may use the app only after Step 2 marks onboarding.emt.completed. */
export function isEmtOnboardingComplete(profile: Profile | null): boolean {
  return profile?.role === 'emt' && profile?.onboarding?.emt?.completed === true;
}

type AuthCtx = {
  user: User | null;
  role: Role;
  profile: Profile | null;
  loading: boolean;
  /** True after the first Firestore profile snapshot for the current auth user (or no user). */
  profileReady: boolean;
  setDisplayName: (name: string) => Promise<void>;
};

const Ctx = createContext<AuthCtx>({ 
  user: null, 
  role: null, 
  profile: null, 
  loading: true,
  profileReady: false,
  setDisplayName: async () => {}
});

export const useAuth = () => useContext(Ctx);
export const useAuthCtx = () => useContext(Ctx); // Alias for compatibility

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  // Set during render (not in an effect) so child effects that list candidates in the same
  // commit already see the right viewer — see lib/demoAccounts.ts.
  setDemoViewer(profile as { isDemoAccount?: unknown; isPracticeAccount?: unknown; practiceOrgId?: unknown } | null);
  const [loading, setLoading] = useState(true);
  const [profileReady, setProfileReady] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pathRef = useRef(location.pathname);

  // Keep pathRef current so redirect effect can re-check before navigating (avoids stale redirect after user went to /p/)
  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  // ============================================================
  // ROUTE DEFINITIONS (explicit buckets)
  // ============================================================
  const publicRoutes = ['/', '/privacy', '/terms', '/sms-terms'];
  const emtRoutesPrefix = ['/dashboard', '/account', '/profile', '/settings', '/performance', '/jobs', '/applications', '/messages', '/notifications', '/register', '/feedback', '/orientation'];
  const employerRoutesPrefix = ['/employer'];
  /** Employer can access these while org/profile setup is incomplete */
  const employerSelfServicePrefixes = [
    '/employer/account',
    '/employer/settings',
    '/employer/company-profile',
    '/employer/company-checklist',
    '/employer/checklist',
    '/employer/matches',
    '/employer/company-setup',
    '/employer/team-onboarding',
    '/employer/edit-company',
    '/employer/workforce',
    '/employer/applications',
    '/employer/search',
    '/employer/jobs',
    '/employer/messages',
    '/employer/dashboard',
  ];
  // /register/step2–4 are deliberately NOT auth routes: they are profile-completion pages for an
  // already-complete (signed-in) EMT account, so a completed EMT must never be bounced off them.
  const authRoutes = ['/login', '/signin', '/signup', '/sso/role-selection', '/employer-login', '/employer/choose-plan', '/employer-registration', '/register/step1', '/emt-welcome', '/institute-registration'];

  // Track last route PER ROLE (except auth screens and public routes)
  // NEVER store "/" as lastSeenRoute - it's a public landing page
  // Store separate keys: lastSeenRoute_emt and lastSeenRoute_employer
  // DO NOT save lastSeenRoute for onboarding/registration routes
  useEffect(() => {
    const excludedRoutes = [...publicRoutes, ...authRoutes];
    const protectedInProgressRoutes = ['/register/', '/employer/choose-plan', '/employer-registration', '/employer/company-setup'];

    // Skip if on excluded routes or debug routes
    if (excludedRoutes.includes(location.pathname) || location.pathname.startsWith('/debug')) {
      return;
    }
    
    // Skip if on onboarding/registration routes (don't save these as lastSeenRoute)
    if (protectedInProgressRoutes.some(route => location.pathname.startsWith(route))) {
      return;
    }
    
    // Only save if we have a role and the route matches the role namespace
    if (profile?.role === 'emt' && !location.pathname.startsWith('/employer')) {
      localStorage.setItem('lastSeenRoute_emt', location.pathname + location.search);
    } else if (profile?.role === 'employer' && location.pathname.startsWith('/employer')) {
      localStorage.setItem('lastSeenRoute_employer', location.pathname + location.search);
    }
  }, [location.pathname, location.search, profile?.role]);

  // ============================================================
  // PROFILE LOADING (Firestore is single source of truth)
  // ============================================================
  useEffect(() => {
    // Listeners are created after ensureAuthReady() resolves, so their teardown has to be
    // returned from the effect itself (not from inside the .then — React never sees that).
    let disposed = false;
    let authUnsub: (() => void) | null = null;
    let profileUnsub: (() => void) | null = null;

    ensureAuthReady().then(() => {
      if (disposed) return;
      if (!auth) {
        setLoading(false);
        return;
      }
      authUnsub = onAuthStateChanged(auth, async (u) => {
        setUser(u);
        writeAuthHint(Boolean(u));
        setProfileReady(false);
        
        // Unsubscribe from previous profile listener
        if (profileUnsub) {
          profileUnsub();
          profileUnsub = null;
        }

        if (u) {
          // Clear any stale localStorage role - role should ONLY come from Firestore
          localStorage.removeItem('role');
          
          if (import.meta.env.DEV) console.log('[AuthContext] User authenticated:', {
            uid: u.uid,
            email: u.email
          });
          
          // Stream profile data with onSnapshot for live updates
          // CRITICAL: Profile path is ALWAYS profiles/{uid} - this is the source of truth
          if (!db) {
            console.warn('[AuthContext] Firestore not initialized, skipping profile load');
            setProfile(null);
            setRole(null);
            setProfileReady(true);
            setLoading(false);
            return;
          }
          const profileRef = doc(db, 'profiles', u.uid);
          
          // `attempt` counts re-subscriptions after a denied read (see the error handler).
          const subscribeProfile = (attempt: number) => {
          profileUnsub = onSnapshot(profileRef, (snap) => {
            if (snap.exists()) {
              const data = snap.data();
              setProfile(data);
              // Update role from profile - ONLY source of truth is Firestore
              const r = (data.role as Role) || null;
              setRole(r);
              
              // Log for debugging
              const emtOnboarding = data.onboarding?.emt;
              const employerOnboarding = data.onboarding?.employer;
              const emtCompleted = emtOnboarding?.completed === true;
              // CRITICAL: Only check orgId if role is employer (ignore orgId if role is EMT)
              const employerCompleted = r === 'employer' && (
                employerOnboarding?.completed === true || !!data.orgId
              );
              
              if (import.meta.env.DEV) console.log('[AuthContext] Profile loaded from Firestore:', {
                uid: u.uid,
                email: u.email,
                role: r,
                emtOnboarding: {
                  exists: !!emtOnboarding,
                  completed: emtCompleted,
                  step: emtOnboarding?.step
                },
                employerOnboarding: {
                  exists: !!employerOnboarding,
                  completed: employerCompleted,
                  orgId: r === 'employer' ? data.orgId : null // Only log orgId if employer
                }
              });
              
              // Only save to localStorage for convenience, but never read from it for routing
              if (r) {
                localStorage.setItem('role', r);
                // Ensure employer wallet exists for existing employers (idempotent)
                if (r === 'employer') {
                  import('../lib/wallets/ensureEmployerWallet').then(({ ensureEmployerWallet }) =>
                    ensureEmployerWallet(u.uid)
                  ).catch((e) => console.warn('[AuthContext] ensureEmployerWallet failed:', e));
                }
              } else {
                localStorage.removeItem('role');
              }
            } else {
              setProfile(null);
              setRole(null);
              localStorage.removeItem('role');
              // Prevent stale lastSeenRoute from yanking profileless users around
              localStorage.removeItem('lastSeenRoute_emt');
              localStorage.removeItem('lastSeenRoute_employer');
              
              if (import.meta.env.DEV) console.log('[AuthContext] No profile found for user:', {
                uid: u.uid,
                email: u.email
              });
            }
            setProfileReady(true);
          }, (err) => {
            console.error('[AuthContext] Profile snapshot error:', err);
            // A denied/unauthenticated profile read mid-session means the session lapsed (token no
            // longer accepted). Make that explicit: sign out and send the person to THEIR login with a
            // notice, instead of leaving stale role state that other screens read as "candidate"
            // (an employer once silently landed on the EMT dashboard this way — 2026-09-24).
            const code = String((err as { code?: unknown } | null)?.code || '');
            if (code === 'permission-denied' || code === 'unauthenticated') {
              const lapse = () => {
                const lastRole = localStorage.getItem('role');
                setProfile(null);
                setRole(null);
                setProfileReady(true);
                (auth ? signOut(auth) : Promise.resolve()).catch(() => undefined).finally(() => {
                  navigate(lastRole === 'employer' ? '/employer-login?expired=1' : '/signin?expired=1', { replace: true });
                });
              };
              // A denied read is not proof of a lapsed session. App Check initializes on the window
              // `load` event (lib/firebase.ts), so a page load's first Firestore requests can go out
              // without an App Check token and be denied where App Check is enforced (staging) —
              // seen 2026-09-25 as a fresh sign-in bounced to /employer-login?expired=1 on the very
              // next page load. Verify first: wait for App Check to settle, force an ID-token refresh
              // (fails if the account is gone/disabled — a real lapse), re-read the profile once, and
              // only then sign out if it is still denied. On success, re-subscribe (an errored
              // listener is dead).
              if (attempt === 0) {
                void (async () => {
                  try {
                    await appCheckSettled;
                    await new Promise((r) => setTimeout(r, 1500));
                    await u.getIdToken(true);
                    await getDoc(profileRef);
                    if (disposed) return;
                    console.warn('[AuthContext] Profile read denied once, readable again — transient (App Check / token timing), re-subscribing.');
                    if (profileUnsub) profileUnsub();
                    subscribeProfile(attempt + 1);
                  } catch (retryErr) {
                    if (disposed) return;
                    console.error('[AuthContext] Profile still unreadable after verification — treating as a lapsed session:', retryErr);
                    lapse();
                  }
                })();
                return;
              }
              lapse();
              return;
            }
            // Fallback to getDoc
            getDoc(profileRef).catch(() => null).then((snap) => {
              if (!snap) {
                setProfileReady(true);
                return;
              }
              if (snap.exists()) {
                const data = snap.data();
                setProfile(data);
                const r = (data.role as Role) || null;
                setRole(r);
                
                if (import.meta.env.DEV) console.log('[AuthContext] Profile loaded via getDoc fallback:', {
                  uid: u.uid,
                  email: u.email,
                  role: r
                });
                
                if (r) {
                  localStorage.setItem('role', r);
                  if (r === 'employer') {
                    import('../lib/wallets/ensureEmployerWallet').then(({ ensureEmployerWallet }) =>
                      ensureEmployerWallet(u.uid)
                    ).catch((e) => console.warn('[AuthContext] ensureEmployerWallet failed:', e));
                  }
                } else {
                  localStorage.removeItem('role');
                }
              } else {
                setProfile(null);
                setRole(null);
                localStorage.removeItem('role');
                localStorage.removeItem('lastSeenRoute_emt');
                localStorage.removeItem('lastSeenRoute_employer');
              }
              setProfileReady(true);
            });
          });
          };
          subscribeProfile(0);
        } else {
          setRole(null);
          setProfile(null);
          localStorage.removeItem('role');
          setProfileReady(true);
        }
        setLoading(false);
      });
    });

    return () => {
      disposed = true;
      if (authUnsub) authUnsub();
      if (profileUnsub) profileUnsub();
    };
  }, []);

  // Redirect loop protection
  const redirectLoopRef = useRef<{ lastTarget: string; count: number; ts: number }>({
    lastTarget: '',
    count: 0,
    ts: 0,
  });

  // ============================================================
  // DETERMINISTIC ROUTING (based ONLY on Firestore profile)
  // ============================================================
  useEffect(() => {
    let cancelled = false;
    pathRef.current = location.pathname;

    const safeNav = (to: string, reason: string) => {
      if (cancelled) return;
      if (pathRef.current.startsWith('/p/')) return;
      if (import.meta.env.DEV) console.log('[NAV_REDIRECT]', reason, '=>', to, 'from', pathRef.current);
      navigate(to, { replace: true });
    };

    if (loading) return;
    if (!user) return; // No user = no redirect needed
    // Wait for first profile snapshot so we don't bounce on a stale/missing profile race
    if (!profileReady) return;

    // Public routes: NEVER redirect
    if (publicRoutes.includes(location.pathname)) {
      return;
    }

    // Debug routes: don't redirect
    if (location.pathname.startsWith('/debug')) {
      return;
    }
    
    // Navigation lock: Skip redirects if user is performing an action (e.g., applying)
    const navLock = sessionStorage.getItem('navLock');
    if (navLock) {
      if (import.meta.env.DEV) console.log('[AuthContext] Navigation lock active, skipping redirect:', navLock);
      return;
    }

    // Registration in progress: keep user on onboarding until profile is complete
    const regInProgress = sessionStorage.getItem('regInProgress');
    if (regInProgress === 'emt' && profile?.role === 'emt' && !isEmtOnboardingComplete(profile)) {
      // Only the account step (step 1) can be incomplete here — steps 2–4 run on a completed account.
      const onboardingPath = '/register/step1';
      if (
        !location.pathname.startsWith('/register/') &&
        location.pathname !== '/sso/role-selection'
      ) {
        safeNav(onboardingPath, 'EMT registration in progress');
        return;
      }
    }
    if (regInProgress && location.pathname.startsWith('/register/')) {
      return;
    }

    // Protected in-progress routes: Don't override navigation during onboarding/registration
    const protectedInProgressRoutes = ['/register/step1', '/register/step2', '/employer/choose-plan', '/employer-registration', '/employer/company-setup', '/sso/role-selection'];
    if (protectedInProgressRoutes.some(route => location.pathname.startsWith(route))) {
      // Only redirect if user is unauthorized (wrong role), not for incomplete onboarding
      // This allows users to complete onboarding without being bounced
      if (profile?.role === 'employer' && location.pathname.startsWith('/register/')) {
        if (import.meta.env.DEV) console.log('[AuthContext] Employer on EMT route during onboarding');
        safeNav('/employer/dashboard', 'employer on EMT register');
        return;
      }
      if (profile?.role === 'emt' && location.pathname.startsWith('/employer')) {
        if (import.meta.env.DEV) console.log('[AuthContext] EMT on employer route during onboarding');
        safeNav('/dashboard', 'EMT on employer route');
        return;
      }
      // Otherwise, let them complete onboarding
      return;
    }
    
    // Wait for profile to load before making routing decisions
    if (!profile) {
      if (import.meta.env.DEV) console.log('[AuthContext] No profile found yet - waiting for profile to be created');
      return;
    }
    
    // Get role and onboarding state from Firestore profile (single source of truth)
    const profileRole = profile?.role;
    
    // Profile integrity: ignore orgId if role is EMT, ignore EMT onboarding if role is employer
    const emtOnboarding = profileRole === 'emt' ? profile?.onboarding?.emt : null;
    const employerOnboarding = profileRole === 'employer' ? profile?.onboarding?.employer : null;
    
    const emtCompleted = isEmtOnboardingComplete(profile);
    const emtStep = Number(emtOnboarding?.step ?? 1);
    
    // CRITICAL: Only check orgId if role is employer (ignore orgId if role is EMT)
    const employerCompleted = profileRole === 'employer' && (
      employerOnboarding?.completed === true || !!profile?.orgId
    );
    
    // Whitelist: /p/:id is viewable by any role (and optionally unauthed). Never redirect away.
    if (location.pathname.startsWith('/p/')) {
      if (import.meta.env.DEV) console.log('[AuthContext] Allowing /p/ route, no redirect', { path: location.pathname });
      return;
    }

    // Public share links (a job's listing, an employer's job board) are for anyone. "Employers can
    // never access EMT routes" below would bounce an employer off /jobs/:jobId — their OWN share
    // link — to the dashboard; PublicJobGate shows them the public posting instead. Candidates are
    // not exempted: for them /jobs/:jobId is the in-app page and the rules below still apply.
    if (profileRole !== 'emt' && isPublicSharePath(location.pathname)) {
      return;
    }

    // Arevina's institute review link (/review/institute?i=&t=) is opened from an email by whoever
    // is signed in at the time — an institute account included, which the institute branch below
    // would otherwise bounce to its dashboard. The page authorises itself server-side.
    if (location.pathname === '/review/institute') {
      return;
    }

    // Shared route: /community is allowed for both EMT and employer. Never redirect away.
    if (location.pathname === '/community') {
      if (import.meta.env.DEV) console.log('[AuthContext] Allowing /community, no redirect', { profileRole });
      return;
    }

    const isOnAuthRoute = authRoutes.includes(location.pathname);
    const isOnEMTRoute =
      location.pathname === '/' ||
      emtRoutesPrefix.some((prefix) => location.pathname.startsWith(prefix));
    const isOnEmployerRoute = employerRoutesPrefix.some(prefix => location.pathname.startsWith(prefix)) ||
                               location.pathname === '/employer-registration' ||
                               location.pathname === '/employer-login';
    
    if (import.meta.env.DEV) console.log('[AuthContext] Routing decision:', {
      currentPath: location.pathname,
      profileRole,
      emtCompleted,
      emtStep,
      employerCompleted,
      isOnAuthRoute,
      isOnEMTRoute,
      isOnEmployerRoute
    });
    
    // Platform admin tools (/admin/*): any role with isAdmin or isSuperAdmin (firestore.rules treats a super
    // admin as an admin; a super admin without isAdmin used to be bounced off /admin, and the stats,
    // flags and institutes pages require isSuperAdmin anyway).
    if (
      location.pathname.startsWith('/admin') &&
      ((profile as { isAdmin?: boolean }).isAdmin || (profile as { isSuperAdmin?: boolean }).isSuperAdmin)
    ) {
      return;
    }

    // ============================================================
    // ROLE-BASED ROUTING LOGIC
    // ============================================================
    
    // ----- EMPLOYER ROUTING -----
    if (profileRole === 'employer') {
      // Allow company setup / account routes even when orgId or onboarding flag is incomplete
      if (employerSelfServicePrefixes.some((prefix) => location.pathname.startsWith(prefix))) {
        return;
      }
      // Completed employers may use any /employer/* app route (workforce, applications, etc.)
      if (isOnEmployerRoute && employerCompleted) {
        return;
      }
      // Plans disabled: never park on choose-plan (route also redirects; avoid allow-stay loops)
      if (location.pathname === '/employer/choose-plan' && !plansEnabled()) {
        const targetPath = employerCompleted ? '/employer/dashboard' : '/employer-registration';
        if (location.pathname !== targetPath) {
          safeNav(targetPath, 'plans disabled — leave choose-plan');
        }
        return;
      }
      // Completed employer on choose-plan: allow (change plan from account); do not redirect
      if (location.pathname === '/employer/choose-plan' && employerCompleted && plansEnabled()) {
        return;
      }
      // Block EMT routes - employers can NEVER access EMT routes
      if (isOnEMTRoute) {
        const targetPath = employerCompleted ? '/employer/dashboard' : '/employer-registration';
        safeNav(targetPath, 'employer on EMT route');
        return;
      }
      
      // Route based on orgId existence (onboarding is completed during registration)
      // If orgId missing, redirect to registration (not onboarding)
      if (isOnAuthRoute || !employerCompleted) {
        // Allow employer to stay on choose-plan when changing plan from account (plans UI on)
        if (location.pathname === '/employer/choose-plan' && employerCompleted && plansEnabled()) {
          return;
        }
        const targetPath = employerCompleted ? '/employer/dashboard' : '/employer-registration';

        // Don't redirect if already on target
        if (location.pathname === targetPath) {
          return;
        }
        
        // Redirect loop protection
        const now = Date.now();
        if (redirectLoopRef.current.lastTarget === targetPath) {
          if (now - redirectLoopRef.current.ts < 2000) {
            redirectLoopRef.current.count++;
            if (redirectLoopRef.current.count >= 2) {
              console.warn('[AuthContext] Redirect loop prevented:', { targetPath, currentPath: location.pathname });
              return;
            }
          } else {
            redirectLoopRef.current = { lastTarget: targetPath, count: 1, ts: now };
          }
        } else {
          redirectLoopRef.current = { lastTarget: targetPath, count: 1, ts: now };
        }
        
        safeNav(targetPath, 'employer auth/onboarding');
      }
      return;
    }

    // ----- INSTITUTE ROUTING -----
    // Medical/Training Institute accounts only have their own dashboard and the public
    // institute listing pages; everything else (EMT app, employer app, auth screens) sends them home.
    if (profileRole === 'institute') {
      if (location.pathname.startsWith('/institute/') || location.pathname.startsWith('/institutes/')) {
        return;
      }
      // Shared pages an institute legitimately opens: Support (it was bounced off the help page),
      // its own class signup link (/class/:id, which has its own institute message) and public
      // company pages (/org/:id, like /careers/:id already is).
      if (
        location.pathname === '/support' ||
        location.pathname === '/contact' ||
        location.pathname.startsWith('/class/') ||
        location.pathname.startsWith('/org/')
      ) {
        return;
      }
      // Registration writes the profile (role "institute") BEFORE the listing doc. Redirecting here
      // on the first profile snapshot yanked a new signup to an empty dashboard mid-submit; the
      // registration page navigates itself once both writes finish (and bounces returning institutes).
      if (location.pathname === '/institute-registration') {
        return;
      }
      safeNav('/institute/dashboard', 'institute outside institute routes');
      return;
    }

    // ----- EMT ROUTING -----
    if (profileRole === 'emt') {
      // Block employer routes - EMTs can NEVER access employer routes
      if (isOnEmployerRoute) {
        const targetPath = emtCompleted ? '/dashboard' : '/register/step1';
        safeNav(targetPath, 'EMT on employer route');
        return;
      }

      // Incomplete account step: funnel to step 1 (never dashboard). Steps 2–4 are profile
      // completion on a completed account, so they are not a funnel target.
      if (!emtCompleted) {
        const targetPath = '/register/step1';
        if (location.pathname === '/register/step1') {
          return;
        }
        if (location.pathname === targetPath) {
          return;
        }
        safeNav(targetPath, 'EMT incomplete onboarding');
        return;
      }
      
      // Route based on onboarding completion (completed EMTs only below)
      if (isOnAuthRoute) {
        safeNav('/dashboard', 'EMT completed on auth route');
      }
      return;
    }

    // ----- UNKNOWN ROLE ROUTING -----
    // No role in profile: they signed in but never picked EMT / employer. Employer pages and sign-in
    // screens are dead ends for them (the sign-in screen used to bounce to "/", which showed the
    // signed-out marketing page), so send them to the role choice. (/sso/role-selection itself is a
    // protected in-progress route above, so this cannot loop.)
    if (!profileRole) {
      // The sign-up pages are how they pick a role, so those stay reachable.
      const isOnSignupChoice = location.pathname === '/signup' || location.pathname === '/institute-registration';
      if ((isOnEmployerRoute || isOnAuthRoute) && !isOnSignupChoice) {
        safeNav('/sso/role-selection', 'no role yet');
      }
      return;
    }

    return () => {
      cancelled = true;
    };
  }, [profile, role, loading, profileReady, location.pathname, navigate, user]);

  // ============================================================
  // LASTSEENROUTE RESTORATION (per-role, validated)
  // ============================================================
  useEffect(() => {
    if (loading || !profileReady || !user || !profile) return;
    if (location.pathname.startsWith('/p/')) return; // Never restore (navigate) when viewing public profile

    const profileRole = profile?.role;
    if (!profileRole) return;
    // Institute accounts have a single home; never restore an EMT/employer lastSeenRoute for them.
    if (profileRole === 'institute') return;

    const isOnAuthRoute = authRoutes.includes(location.pathname);
    if (!isOnAuthRoute) return;

    // EMT registration: never restore lastSeenRoute (often stale "/dashboard") while onboarding is incomplete —
    // that was incorrectly sending users from Step 2 back to the dashboard mid-signup.
    if (profileRole === 'emt' && !isEmtOnboardingComplete(profile)) {
      return;
    }

    // Never yank users off employer registration / choose-plan mid-signup via lastSeenRoute.
    // When plans are disabled, choose-plan is not a valid mid-signup park — let routing send them onward.
    if (
      location.pathname === '/employer-registration' ||
      (location.pathname === '/employer/choose-plan' && plansEnabled()) ||
      location.pathname === '/employer/company-setup'
    ) {
      return;
    }

    // Incomplete employer: stay on registration flow; do not restore a prior dashboard/settings URL.
    const employerCompleted = profileRole === 'employer' && (profile?.onboarding?.employer?.completed === true || !!profile?.orgId);
    if (profileRole === 'employer' && !employerCompleted) {
      return;
    }

    // Completed employer on choose-plan: they came to change plan; do not restore to lastSeenRoute (e.g. account)
    if (location.pathname === '/employer/choose-plan' && employerCompleted && plansEnabled()) return;
    
    // Get role-specific lastSeenRoute
    const roleKey = profileRole === 'employer' ? 'lastSeenRoute_employer' : 'lastSeenRoute_emt';
    const last = localStorage.getItem(roleKey);
    if (!last) return;
    
    // Validate lastSeenRoute against role namespace
    const isValidForRole = profileRole === 'employer' 
      ? last.startsWith('/employer') || last === '/company-profile'
      : !last.startsWith('/employer') && !last.startsWith('/company-profile');
    
    if (!isValidForRole) {
      if (import.meta.env.DEV) console.log('[AuthContext] Ignoring lastSeenRoute - does not match role namespace:', {
        lastSeenRoute: last,
        role: profileRole,
        roleKey
      });
      return;
    }
    
    // Don't restore if it's another auth route
    if (authRoutes.some(route => last.startsWith(route))) {
      return;
    }
    
    if (import.meta.env.DEV) console.log('[AuthContext] lastSeenRoute restore =>', last, 'role:', roleKey);
    navigate(last, { replace: true });
  }, [loading, profileReady, user, profile, location.pathname, navigate]);

  const setDisplayName = async (name: string) => {
    if (!auth || !auth.currentUser) return;
    await updateProfile(auth.currentUser, { displayName: name });
  };

  const value = useMemo(
    () => ({ user, role, profile, loading, profileReady, setDisplayName }),
    [user, role, profile, loading, profileReady],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
